import { useState } from "react";
import { Pencil, Trash2, Tag, Plus } from "lucide-react";
import {
    Button,
    Container,
    DataTable,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogForm,
    DialogHeader,
    DialogTitle,
    Field,
    FieldGroup,
    FieldLabel,
    FieldSet,
    Input,
    Mono,
    SectionHeader,
    TableSkeleton,
    Text,
} from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { useQuery, useMutation } from "@apollo/client/react";
import { toast } from "sonner";
import {
    TagsDocument,
    CreateTagDocument,
    UpdateTagDocument,
    DeleteTagDocument,
    type TagsQuery,
} from "../../graphql/tags.generated";
import { ErrorFallback } from "@/components/ErrorFallback";
import { DeleteDialog } from "@/components/DeleteDialog";
import { PAGE_SIZE } from "../../../../global/constants";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";

type Tag = TagsQuery["tags"][number];

export function TagsPage() {
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        selected,
        setSelected,
    ] = useState<Set<string>>(new Set());
    const [
        newTagOpen,
        setNewTagOpen,
    ] = useState<boolean>(false);
    const [
        editTag,
        setEditTag,
    ] = useState<{ id: string; name: string } | null>(null);
    const [
        editName,
        setEditName,
    ] = useState<string>("");
    const [
        deleteTag,
        setDeleteTag,
    ] = useState<Pick<Tag, "id" | "name" | "courses"> | null>(null);

    const { data, loading, error, refetch } = useQuery(TagsDocument);
    // No refetch on update/delete: the mutation returns the tag, and Apollo's normalized
    // cache updates the row from it. Only create needs the list re-read, since a new tag
    // isn't in the previous result.
    const [createTag] = useMutation(CreateTagDocument, {
        onCompleted: () => void refetch(),
    });
    const [
        updateTag,
        { loading: updating },
    ] = useMutation(UpdateTagDocument);
    const [
        deleteTagMutation,
        { loading: deleting },
    ] = useMutation(DeleteTagDocument);

    const tags = data?.tags ?? [];
    const filtered = tags.filter((tag) => {
        return tag.name.toLowerCase().includes(search.toLowerCase());
    });

    /*
     * Scroll or pages, the reader's choice, portal-wide — see `useListView`.
     *
     * This page had its own page state and its own Previous/Next, over a list it already held
     * whole and sliced. Same slice, one control, and the reader can now ask for all of it. Above
     * the guards below because it calls hooks: after an early return it would be skipped on the
     * loading render and called on the next one, which is a different hook order.
     */
    const view = useListView(filtered, { pageSize: PAGE_SIZE });

    // Keyed on data, not `loading`: Apollo sets loading on every refetch, so gating
    // the whole page on it replaces live content — and unmounts any child holding
    // state or an in-flight request — every time something reloads.
    if (loading && !data) return <TableSkeleton />;
    if (error) {
        return (
            <Container maxWidth="full" className="p-6">
                <ErrorFallback error={error} />
            </Container>
        );
    }
    const totalTags = tags.length;

    const handleCreate = async ({ name }: { name: string }) => {
        await createTag({ variables: { input: { name: name.trim() } } });
    };

    const openEdit = (tag: { id: string; name: string }) => {
        setEditTag(tag);
        setEditName(tag.name);
    };

    const handleUpdate = async () => {
        if (!editTag || !editName.trim()) return;
        await updateTag({
            variables: { id: editTag.id, input: { name: editName.trim() } },
            optimisticResponse: {
                updateTag: { __typename: "Tag", id: editTag.id, name: editName.trim() },
            },
        });
        setEditTag(null);
    };

    const deleteBlockedReason =
        deleteTag && deleteTag.courses.length > 0
            ? `Still assigned to ${deleteTag.courses.length} course${deleteTag.courses.length === 1 ? "" : "s"}: ${deleteTag.courses.map((c) => c.title).join(", ")}. Remove it from ${deleteTag.courses.length === 1 ? "that course" : "those courses"} first.`
            : null;

    const handleDelete = async () => {
        if (!deleteTag) return;
        try {
            await deleteTagMutation({
                variables: { id: deleteTag.id },
                update(cache) {
                    cache.evict({ id: cache.identify({ __typename: "Tag", id: deleteTag.id }) });
                    cache.gc();
                },
            });
            setSelected((prev) => {
                const next = new Set(prev);
                next.delete(deleteTag.id);
                return next;
            });
            setDeleteTag(null);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Couldn't delete this tag. Please try again.");
        }
    };

    return (
        <Container className="p-6">
            <SectionHeader
                eyebrow={`Content · ${totalTags} Tags`}
                title={"Tags"}
                description={
                    "Organize courses into themes learners can browse in the app. Tags can live on one or both platforms."
                }
                actions={
                    <Button size="sm" className="flex items-center gap-1.5" onClick={() => setNewTagOpen(true)}>
                        <Plus className="h-3.5 w-3.5" />
                        New Tag
                    </Button>
                }
            />

            {/* Filters + pagination label */}
            <div className="mb-4 flex items-center gap-3">
                <div className="relative max-w-xs flex-1">
                    <Tag className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
                    <Input
                        placeholder="Search tags..."
                        className="bg-card h-9 pl-9 text-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        type="text"
                    />
                </div>
            </div>

            {/* Table */}
            {/* Above the table, where Becca asked for it: "the top of the table row or above it". */}
            <ListViewControls
                view={view}
                noun="tags"
                count={filtered.length === 0 ? "0 results" : `${view.from}–${view.to} of ${filtered.length}`}
            />

            <DataTable
                columns={[
                    {
                        key: "name",
                        header: "Name",
                        cell: (tag) => (
                            <Text weight="medium" className="text-sm">
                                {tag.name}
                            </Text>
                        ),
                    },
                    {
                        key: "courses",
                        header: "Courses",
                        headerClassName: "text-right",
                        className: "text-right",
                        cell: (tag) => (
                            <Mono
                                className={cn(
                                    "text-sm",
                                    tag.courses.length === 0 ? "text-muted-foreground" : "text-foreground",
                                )}
                            >
                                {tag.courses.length}
                            </Mono>
                        ),
                    },
                ]}
                rows={view.rows}
                rowKey={(tag) => tag.id}
                rowSelected={(tag) => selected.has(tag.id)}
                filtered={totalTags > 0}
                empty={
                    <div className="py-12 text-center">
                        <Text weight="medium" className="text-sm">
                            No tags yet. Add one now to start organizing your courses.
                        </Text>
                    </div>
                }
                noMatches="No tags match your filters."
                actions={(tag) => (
                    <div className="flex items-center justify-end gap-1">
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-foreground"
                            aria-label={`Edit ${tag.name}`}
                            onClick={() => openEdit(tag)}
                        >
                            <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={`Delete ${tag.name}`}
                            onClick={() => setDeleteTag(tag)}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )}
            />

            <div className="mt-4">
                <Mono className="text-muted-foreground text-xs">
                    {selected.size > 0 ? `${selected.size} selected` : ""}
                </Mono>
            </div>
            <DialogForm
                open={newTagOpen}
                onOpenChange={setNewTagOpen}
                title="New tag"
                description="Tags are shared across courses and help learners discover content."
                showAvatar={false}
                showTitleRole={false}
                showBio={false}
                labelName="Tag name"
                labelNamePlaceholder="e.g. Anxiety"
                labelCancel="Cancel"
                labelSubmit="Add tag"
                onSubmit={handleCreate}
            ></DialogForm>

            {/* Edit dialog */}
            <Dialog
                open={!!editTag}
                onOpenChange={(open) => {
                    if (!open) setEditTag(null);
                }}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Edit tag</DialogTitle>
                        <DialogDescription>Update the tag name. This affects all courses using it.</DialogDescription>
                    </DialogHeader>
                    <FieldSet>
                        <FieldGroup>
                            <Field>
                                <FieldLabel htmlFor="edit-tag-name" required>
                                    Tag name
                                </FieldLabel>
                                <Input
                                    id="edit-tag-name"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleUpdate();
                                    }}
                                    autoFocus
                                    type="text"
                                />
                            </Field>
                        </FieldGroup>
                    </FieldSet>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditTag(null)}>
                            Cancel
                        </Button>
                        <Button disabled={!editName.trim() || updating} onClick={handleUpdate}>
                            {updating ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DeleteDialog
                open={!!deleteTag}
                onOpenChange={(o) => {
                    if (!o) setDeleteTag(null);
                }}
                title="Delete Tag"
                deleteTarget={deleteTag?.name}
                onConfirm={handleDelete}
                loading={deleting}
                blockedReason={deleteBlockedReason}
            />
        </Container>
    );
}
